import type { LevelConfig } from '../config/level.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { Random } from '../utils/Random';
import type { ConnectorDefinition } from './connectors/ConnectorDefinition';
import { alignToExit, connectorWorldPose } from './connectors/ConnectorMath';
import { IDENTITY_POSE, type Pose } from './connectors/Pose';
import { chainRunAfter, sidewaysSide, sightLineAfter, type ChainRun, type LevelGenerator } from './generation/LevelGenerator';
import type { SegmentCatalog } from './segments/SegmentCatalog';
import { laneCenterX } from './segments/segmentBuilders';
import type { SegmentDefinition, SegmentTerrain } from './segments/SegmentDefinition';
import { SegmentInstance, type WorldFrame } from './segments/SegmentInstance';
import type { PopulateContext, SegmentPopulator, SequenceHistory } from './spawning/SegmentPopulator';
import type { SafeGround, TerrainQuery } from './terrain/TerrainQuery';
import * as terrainQueries from './terrain/terrainQueries';

/** What the populator learns about the module after a segment (picked one module ahead, see extend). */
type LookAhead = (segment: SegmentInstance) => Pick<PopulateContext, 'next' | 'nextUnknown'>;

/** Body dimensions the safe-ground search needs (after a fall into a pit). */
export interface RunnerBody {
  readonly width: number;
  readonly standingHeight: number;
  readonly maxStepHeight: number;
}

/** The placement's terrain – as the run changed it (a pit filled by the dozer). */
const terrainOf = (segment: SegmentInstance): SegmentTerrain => segment.terrain ?? terrainQueries.EMPTY_TERRAIN;

/** Point on the track centerline in world space plus the local track heading. */
export interface TrackFrame extends WorldFrame {
  segment: SegmentInstance | null;
}

export function trackFrame(): TrackFrame {
  return { x: 0, y: 0, z: 0, yaw: 0, segment: null };
}

/** Open decision at a T-junction/crossing: every exit has a pre-built preview branch. */
export interface PendingBranch {
  readonly junction: SegmentInstance;
  readonly branches: ReadonlyMap<string, readonly SegmentInstance[]>;
}

export interface TrackManagerDeps {
  readonly catalog: SegmentCatalog;
  readonly generator: LevelGenerator;
  readonly populator: SegmentPopulator;
  readonly events: EventBus<GameEvents>;
  readonly level: LevelConfig;
  readonly world: WorldConfig;
}

/**
 * Maintains the endless chain of segments around the player: spawns ahead,
 * recycles behind, builds preview branches at intersections and maps track
 * distance ↔ world space. Pure logic – visuals react to segment events.
 *
 * Committed chain = the path the player is on (or will be on). Generation
 * pauses at an undecided intersection; its preview branches live in
 * `previews` until the TurnController commits one of them.
 *
 * Also the track's terrain in track space (TerrainQuery): segment surfaces,
 * pits and ceilings of committed segments, stitched across segment borders.
 */
export class TrackManager implements TerrainQuery {
  private readonly active: SegmentInstance[] = [];
  private readonly previews: SegmentInstance[] = [];
  private branch: PendingBranch | null = null;
  private readonly branchHistories = new Map<string, SequenceHistory>();
  /** Abandoned branch segments → track distance of their intersection's end. */
  private readonly orphans = new Map<SegmentInstance, number>();
  private nextUid = 1;
  private nextSequenceIndex = 0;
  private segmentsSinceJunction = 0;
  /** Chain run at the end of the committed chain (pendulum cranes in a row, LevelGenerator). */
  private chainRun: ChainRun | null = null;
  /** Meters of open straight at the end of the committed chain (landmark sight line, LevelGenerator). */
  private sightLineApproach = 0;
  /**
   * Meters of plain straights the run still owes its scheduled set pieces (PlainDebt, LevelGenerator): with the
   * dozer every ≈ 600 m and the golden ramp the 60 m set pieces fill ≈ 15 % of the track – they take it from the
   * plain straights, every other module keeps its frequency per km.
   */
  private plainDebt = 0;
  private readonly lastPlaced = new Map<string, number>();
  /** Committed placements per segment id in this run (set-piece schedule, LevelGenerator). */
  private readonly placementCount = new Map<string, number>();
  /** Start-axis z of the latest committed stretch heading sideways (±90°) per side – reversal rule. */
  private readonly sideways = new Map<-1 | 1, number>();
  /**
   * The module after the last committed one, picked one module ahead (extend): the populator keeps the drop
   * zones behind containers at the end of a segment clear of what follows. Undefined after an intersection.
   */
  private upcoming: SegmentDefinition | undefined;
  private baseSeed = 1;
  /** Separate streams: track layout, gameplay placement, cosmetics. */
  private topologyRng = new Random(1);
  private gameplayRng = new Random(1);
  private cosmeticRng = new Random(1);

  constructor(private readonly deps: TrackManagerDeps) {}

  /** Committed segments in path order. */
  get segments(): readonly SegmentInstance[] {
    return this.active;
  }

  /** Preview segments of open/abandoned intersection branches. */
  get previewSegments(): readonly SegmentInstance[] {
    return this.previews;
  }

  get pendingBranch(): PendingBranch | null {
    return this.branch;
  }

  get seed(): number {
    return this.baseSeed;
  }

  /** Starts a new track from the origin. Emits recycle events for the previous track. */
  start(seed: number): void {
    this.clear();
    this.baseSeed = seed >>> 0;
    this.topologyRng = new Random(Random.deriveSeed(this.baseSeed, 1));
    this.gameplayRng = new Random(Random.deriveSeed(this.baseSeed, 2));
    this.cosmeticRng = new Random(Random.deriveSeed(this.baseSeed, 3));
    this.deps.populator.reset();
    this.segmentsSinceJunction = 0;
    this.chainRun = null;
    this.sightLineApproach = 0;
    this.plainDebt = 0;
    this.lastPlaced.clear();
    this.placementCount.clear();
    this.sideways.clear();
    this.spawn(this.deps.catalog.get(this.deps.level.startSegmentId), IDENTITY_POSE, 0, { committed: true, safe: true });
    this.update(0);
  }

  /** Spawns segments ahead of and recycles segments behind the given track distance. */
  update(playerDistance: number): void {
    const { level, world } = this.deps;
    const lookAhead = level.segmentsAhead * world.baseSegmentLength;
    const keepBehind = level.segmentsBehind * world.baseSegmentLength;

    let last = this.active[this.active.length - 1];
    while (last && !this.branch && last.endDistance < playerDistance + lookAhead) {
      last = this.extend(last);
    }

    while (this.active.length > 1) {
      const first = this.active[0] as SegmentInstance;
      if (first.endDistance >= playerDistance - keepBehind) break;
      this.active.shift();
      this.deps.events.emit('segment:recycled', { segment: first });
    }

    // Abandoned branches disappear together once the player is past their intersection.
    for (const [orphan, junctionEnd] of this.orphans) {
      if (junctionEnd >= playerDistance - keepBehind) continue;
      this.orphans.delete(orphan);
      this.previews.splice(this.previews.indexOf(orphan), 1);
      this.deps.events.emit('segment:recycled', { segment: orphan });
    }
  }

  /**
   * Commits the preview branch behind `exit` of the pending intersection:
   * it becomes part of the path, the other branches are abandoned.
   */
  commitBranch(exit: ConnectorDefinition): void {
    const pending = this.branch;
    if (!pending) return;
    const chosen = pending.branches.get(exit.id);
    if (!chosen) throw new Error(`Junction ${pending.junction.definition.id} has no branch for ${exit.id}`);
    pending.junction.continuation = exit;
    this.noteSideways(pending.junction, exit);
    for (const [exitId, list] of pending.branches) {
      if (exitId === exit.id) continue;
      for (const segment of list) this.orphans.set(segment, pending.junction.endDistance);
    }
    for (const segment of chosen) {
      segment.committed = true;
      this.previews.splice(this.previews.indexOf(segment), 1);
      this.active.push(segment);
    }
    this.segmentsSinceJunction = chosen.length;
    this.chainRun = null; // junction and plain preview straights: no run going on
    this.sightLineApproach = chosen.reduce((open, segment) => sightLineAfter(open, segment.definition), 0);
    this.deps.populator.sequenceHistory = this.branchHistories.get(exit.id) ?? null;
    this.branchHistories.clear();
    this.branch = null;
  }

  segmentAt(distance: number): SegmentInstance | null {
    for (const segment of this.active) if (segment.containsDistance(distance)) return segment;
    return null;
  }

  /** World-space centerline frame at a track distance (extrapolates before/after the chain). */
  sampleFrame(distance: number, out: TrackFrame = trackFrame()): TrackFrame {
    return this.trackToWorld(0, 0, distance, out);
  }

  /** Track space (lateral x, height y, distance) → world position + heading. */
  trackToWorld(x: number, y: number, distance: number, out: TrackFrame = trackFrame()): TrackFrame {
    const first = this.active[0];
    const last = this.active[this.active.length - 1];
    const segment = this.segmentAt(distance) ?? (first && distance < first.startDistance ? first : last ?? null);
    out.segment = segment;
    if (!segment) {
      out.x = x;
      out.y = y;
      out.z = distance;
      out.yaw = 0;
      return out;
    }
    segment.trackToWorld(x, y, distance, out);
    return out;
  }

  // ── Terrain (track space) ────────────────────────────────────────────────

  /** Ground height at a track position, or null where there is no ground (pits, off the track). */
  groundHeightAt(distance: number, lateral: number): number | null {
    const segment = this.segmentAt(distance);
    if (!segment) return null;
    return terrainQueries.groundHeightAt(terrainOf(segment), this.deps.world.groundHeight, segment.localZ(distance), lateral);
  }

  highestGroundIn(minZ: number, maxZ: number, minX: number, maxX: number, topReach = terrainQueries.NO_TOP_REACH): number | null {
    let top: number | null = null;
    for (const segment of this.active) {
      if (segment.endDistance <= minZ || segment.startDistance >= maxZ) continue;
      const from = Math.max(minZ, segment.startDistance) - segment.startDistance;
      const to = Math.min(maxZ, segment.endDistance) - segment.startDistance;
      const height = terrainQueries.highestGroundIn(terrainOf(segment), this.deps.world.groundHeight, from, to, minX, maxX, topReach);
      if (height !== null) top = top === null ? height : Math.max(top, height);
    }
    return top;
  }

  lowestCeilingIn(
    minZ: number,
    maxZ: number,
    minX: number,
    maxX: number,
    minHeight: number,
    filter: terrainQueries.CeilingFilter = 'all',
    topReach = terrainQueries.NO_TOP_REACH,
  ): number | null {
    let lowest: number | null = null;
    for (const segment of this.active) {
      if (segment.endDistance <= minZ || segment.startDistance >= maxZ) continue;
      const terrain = terrainOf(segment);
      if (terrain.ceilings.length === 0) continue;
      const from = Math.max(minZ, segment.startDistance) - segment.startDistance;
      const to = Math.min(maxZ, segment.endDistance) - segment.startDistance;
      const clearance = terrainQueries.lowestCeilingIn(terrain, from, to, minX, maxX, minHeight, filter, topReach);
      if (clearance !== null) lowest = lowest === null ? clearance : Math.min(lowest, clearance);
    }
    return lowest;
  }

  pitFloorAt(distance: number, lateral: number): number | null {
    const segment = this.segmentAt(distance);
    if (!segment) return null;
    return terrainQueries.pitFloorAt(terrainOf(segment), this.deps.world.groundHeight, segment.localZ(distance), lateral);
  }

  /**
   * Safe spot to put a fallen runner down: the first stretch of level ground (no pit,
   * enough head room) ahead of `distance`, 2 × respawnMargin long – preferably in the
   * runner's own lane. Returns the middle of that stretch, or null if none is in reach.
   */
  findSafeGround(distance: number, lateral: number, body: RunnerBody): SafeGround | null {
    const { world, level } = this.deps;
    const { respawnMargin, respawnSearchDistance, sampleStep } = level.terrain;
    const half = body.width / 2;
    const lanes = Array.from({ length: world.laneCount }, (_, lane) => laneCenterX(world, lane)).sort(
      (a, b) => Math.abs(a - lateral) - Math.abs(b - lateral),
    );
    for (const x of lanes) {
      let runStart: number | null = null;
      let low = Infinity;
      let high = -Infinity;
      for (let s = distance; s <= distance + respawnSearchDistance; s += sampleStep) {
        const ground = this.highestGroundIn(s, s + sampleStep, x - half, x + half);
        const ceiling = this.lowestCeilingIn(s, s + sampleStep, x - half, x + half, -Infinity, 'hard');
        if (ground === null || (ceiling !== null && ceiling - ground < body.standingHeight)) {
          runStart = null;
          continue;
        }
        if (runStart !== null && Math.max(high, ground) - Math.min(low, ground) > body.maxStepHeight) {
          runStart = null; // not level – a new stretch starts here
        }
        if (runStart === null) {
          runStart = s;
          low = high = ground;
        }
        low = Math.min(low, ground);
        high = Math.max(high, ground);
        if (s + sampleStep - runStart >= 2 * respawnMargin) {
          const at = runStart + respawnMargin;
          return { distance: at, x, y: this.groundHeightAt(at, x) ?? ground };
        }
      }
    }
    return null;
  }

  clear(): void {
    for (const segment of [...this.active, ...this.previews]) this.deps.events.emit('segment:recycled', { segment });
    this.active.length = 0;
    this.previews.length = 0;
    this.branch = null;
    this.branchHistories.clear();
    this.orphans.clear();
    this.nextSequenceIndex = 0;
    this.upcoming = undefined;
  }

  // ── Generation ───────────────────────────────────────────────────────────

  private extend(previous: SegmentInstance): SegmentInstance {
    const exit = previous.continuation;
    if (!exit) {
      this.createBranches(previous);
      return previous;
    }
    // picked one module ahead while `previous` was populated – or now (after the start, after an intersection)
    const definition = this.upcoming ?? this.pickAfter(previous, exit);
    this.upcoming = undefined;
    this.lastPlaced.set(definition.id, previous.endDistance);
    this.placementCount.set(definition.id, (this.placementCount.get(definition.id) ?? 0) + 1);
    this.segmentsSinceJunction = definition.junction ? 0 : this.segmentsSinceJunction + 1;
    this.chainRun = chainRunAfter(this.chainRun, previous.definition, definition, previous.endDistance, this.topologyRng.seed);
    this.sightLineApproach = sightLineAfter(this.sightLineApproach, definition);
    if (definition.generation.schedule) this.plainDebt += definition.length;
    return this.spawnAfter(previous, exit, definition, true, (segment) => {
      this.noteSideways(segment, segment.continuation);
      // One module ahead (the same pick, only earlier – the track layout does not depend on the population): a
      // container at the end of this segment never drops its runner onto terrain, a portal or a moving hazard of
      // the next one (SegmentPopulator, PopulateContext.next).
      this.upcoming = segment.continuation ? this.pickAfter(segment, segment.continuation) : undefined;
      return this.upcoming ? { next: this.upcoming } : { nextUnknown: true };
    });
  }

  /** The generator's pick for the module after `previous` (its exit `exit`), with the committed chain's state. */
  private pickAfter(previous: SegmentInstance, exit: ConnectorDefinition): SegmentDefinition {
    return this.deps.generator.pickNext(exit, {
      runDistance: previous.endDistance,
      heading: this.exitHeading(previous, exit),
      segmentsSinceJunction: this.segmentsSinceJunction,
      lastPlacedAt: (id) => this.lastPlaced.get(id),
      placements: (id) => this.placementCount.get(id) ?? 0,
      previousFamily: previous.definition.generation.family,
      chainRun: this.chainRun,
      rng: this.topologyRng,
      exitPose: connectorWorldPose(previous.pose, exit),
      sidewaysAxis: (side) => this.sideways.get(side),
      sightLineApproach: this.sightLineApproach,
      previous: previous.definition,
      plainDebt: { meters: this.plainDebt, pay: (meters) => (this.plainDebt = Math.max(0, this.plainDebt - meters)) },
    });
  }

  /** Remembers where the committed track runs sideways (see LevelGenerator: reversal rule). */
  private noteSideways(segment: SegmentInstance, exit: ConnectorDefinition | null): void {
    if (!exit) return;
    const pose = connectorWorldPose(segment.pose, exit);
    const side = sidewaysSide(pose.yaw);
    if (side !== 0) this.sideways.set(side, pose.z);
  }

  /** Pre-builds straight preview branches on every exit of an intersection. */
  private createBranches(junction: SegmentInstance): void {
    const { populator, catalog, level } = this.deps;
    const baseHistory = populator.sequenceHistory;
    const straight = catalog.get(level.startSegmentId);
    const branches = new Map<string, SegmentInstance[]>();

    for (const exit of junction.definition.exits) {
      populator.sequenceHistory = baseHistory;
      const list: SegmentInstance[] = [];
      let previous = junction;
      let previousExit: ConnectorDefinition = exit;
      for (let i = 0; i < level.junctions.branchPreviewSegments; i++) {
        // behind the last preview module the track is picked only once the branch is taken
        const last = i === level.junctions.branchPreviewSegments - 1;
        const segment = this.spawnAfter(previous, previousExit, straight, false, () => (last ? { nextUnknown: true } : { next: straight }));
        list.push(segment);
        previous = segment;
        previousExit = segment.continuation as ConnectorDefinition;
      }
      branches.set(exit.id, list);
      this.branchHistories.set(exit.id, populator.sequenceHistory);
    }
    populator.sequenceHistory = baseHistory;
    this.branch = { junction, branches };
  }

  private spawnAfter(previous: SegmentInstance, exit: ConnectorDefinition, definition: SegmentDefinition, committed: boolean, ahead?: LookAhead): SegmentInstance {
    const pose = alignToExit(previous.pose, exit, definition.entry);
    // Right after a junction the player is still turning – keep that segment obstacle free.
    const safe = this.nextSequenceIndex < this.deps.level.safeStartSegments || previous.isJunction;
    return this.spawn(definition, pose, previous.endDistance, { committed, safe, afterJunction: previous.isJunction }, ahead);
  }

  private spawn(
    definition: SegmentDefinition,
    pose: Pose,
    startDistance: number,
    options: { readonly committed: boolean; readonly safe: boolean; readonly afterJunction?: boolean },
    ahead?: LookAhead,
  ): SegmentInstance {
    const segment = new SegmentInstance(this.nextUid++, this.nextSequenceIndex++, definition, pose, startDistance);
    segment.committed = options.committed;
    segment.spawnPlan = this.deps.populator.populate(definition, {
      startDistance,
      safe: options.safe,
      rng: this.gameplayRng,
      cosmeticRng: this.cosmeticRng,
      afterJunction: options.afterJunction ?? false,
      ...ahead?.(segment),
    });
    (options.committed ? this.active : this.previews).push(segment);
    this.deps.events.emit('segment:spawned', { segment });
    return segment;
  }

  private exitHeading(segment: SegmentInstance, exit: ConnectorDefinition): number {
    return connectorWorldPose(segment.pose, exit).yaw;
  }

}
